import React, { useState } from "react";
import { Zap, Box, Globe, Users, Target, Search } from "react-feather";
import DetailModal from "./DetailModal";

const MarketPosition = () => {
  const [selectedHeatMapItem, setSelectedHeatMapItem] = useState(null);
  const [selectedDetailModal, setSelectedDetailModal] = useState(null);

  const heatMapData = {
    technologies: [
      {
        name: "LV/MV Switchgear & Busway",
        players: 2,
        companies: ["nVent", "Schneider Electric"],
        color: "custom-warning",
        marketSize: "$73.5B",
        dataKey: "tech-0",
      },
      {
        name: "EMI/Shielding & Thermal Materials",
        players: 1,
        companies: ["Boyd Corporation"],
        color: "custom-success",
        note: "Boyd niche",
        marketSize: "$7.7B",
        dataKey: "tech-1",
      },
      {
        name: "Electrical Enclosures",
        players: 2,
        companies: ["nVent", "Schneider Electric"],
        color: "custom-warning",
        marketSize: "$7.4B",
        dataKey: "tech-2",
      },
      {
        name: "Data Center Liquid Cooling",
        players: 3,
        companies: ["nVent", "Schneider Electric", "Boyd Corporation"],
        color: "custom-info",
        marketSize: "$4.8B",
        dataKey: "tech-3",
      },
      {
        name: "Industrial Heat-Tracing",
        players: 1,
        companies: ["nVent"],
        color: "custom-success",
        note: "nVent leadership",
        marketSize: "$3.4B",
        dataKey: "tech-4",
      },
    ],
    applications: [
      {
        name: "Industry & Automation",
        players: 2,
        companies: ["nVent", "Schneider Electric"],
        color: "custom-warning",
        marketSize: "$276.6B",
        dataKey: "app-0",
      },
      {
        name: "EV Charging Infrastructure",
        players: 2,
        companies: ["nVent", "Schneider Electric"],
        color: "custom-warning",
        marketSize: "$121.9B",
        dataKey: "app-1",
      },
      {
        name: "Grid Modernization & Utilities",
        players: 2,
        companies: ["nVent", "Schneider Electric"],
        color: "custom-warning",
        marketSize: "$103.4B",
        dataKey: "app-2",
      },
      {
        name: "Commercial Buildings",
        players: 2,
        companies: ["nVent", "Schneider Electric"],
        color: "custom-warning",
        marketSize: "$101.7B",
        dataKey: "app-3",
      },
      {
        name: "Data Centers & Networks",
        players: 3,
        companies: ["nVent", "Schneider Electric", "Boyd Corporation"],
        color: "custom-info",
        marketSize: "$41.1B",
        dataKey: "app-4",
      },
    ],
    geographies: [
      {
        name: "Asia Pacific",
        players: 3,
        companies: ["nVent", "Schneider Electric", "Boyd Corporation"],
        color: "custom-info",
        marketSize: "$53.6B",
        dataKey: "geo-0",
      },
      {
        name: "Europe",
        players: 3,
        companies: ["nVent", "Schneider Electric", "Boyd Corporation"],
        color: "custom-info",
        marketSize: "$39.0B",
        dataKey: "geo-1",
      },
      {
        name: "North America",
        players: 3,
        companies: ["nVent", "Schneider Electric", "Boyd Corporation"],
        color: "custom-info",
        marketSize: "$25.0B",
        dataKey: "geo-2",
      },
    ],
    segments: [
      {
        name: "Industrial OEMs & Machine Builders",
        players: 2,
        companies: ["nVent", "Schneider Electric"],
        color: "custom-warning",
        marketSize: "$276.6B",
        dataKey: "seg-0",
      },
      {
        name: "Utilities & Grid Operators",
        players: 2,
        companies: ["nVent", "Schneider Electric"],
        color: "custom-warning",
        marketSize: "$103.4B",
        dataKey: "seg-1",
      },
      {
        name: "Hyperscale & Colocation",
        players: 3,
        companies: ["nVent", "Schneider Electric", "Boyd Corporation"],
        color: "custom-info",
        marketSize: "$79.1B",
        dataKey: "seg-2",
      },
      {
        name: "Electrical Contractors & EPCs",
        players: 2,
        companies: ["nVent", "Schneider Electric"],
        color: "custom-warning",
        marketSize: "$73.5B",
        dataKey: "seg-3",
      },
      {
        name: "Automotive/EV OEMs",
        players: 2,
        companies: ["Schneider Electric", "Boyd Corporation"],
        color: "custom-warning",
        marketSize: "$4.1B",
        dataKey: "seg-4",
      },
    ],
  };

  const focusAreas = {
    schneider: {
      technologies: [
        "LV/MV Switchgear & Busway",
        "Racks/PDUs/UPS",
        "Liquid Cooling (Motivair)",
        "BMS/EMS Software",
      ],
      applications: [
        "Data Centers & Networks",
        "Commercial Buildings",
        "Grid Modernization",
      ],
      geographies: ["North America", "Europe (HQ)", "Asia Pacific"],
      segments: [
        "Hyperscale & Colocation",
        "Electrical Contractors & EPCs",
        "Utilities & Grid Operators",
      ],
      useCases: [
        "AI-ready power & cooling stacks",
        "Microgrid/ADMS deployments",
        "BMS retrofits in large campuses",
      ],
    },
    nvent: {
      technologies: [
        "Busway & LV Distribution (E+I)",
        "Liquid Cooling CDUs/Manifolds",
        "Enclosures (HOFFMAN/SCHROFF)",
        "Heat-Tracing (RAYCHEM)",
      ],
      applications: [
        "Data Centers & Networks",
        "Industrial Automation",
        "Commercial Buildings",
      ],
      geographies: [
        "North America (Primary)",
        "Europe",
        "Asia Pacific (expanding)",
      ],
      segments: [
        "Hyperscale & Colocation",
        "Industrial OEMs & Machine Builders",
        "Electrical Contractors & EPCs",
      ],
      useCases: [
        "Rack-level liquid cooling integration",
        "Modular busway in AI halls",
        "Heat-trace for LNG/pipelines",
      ],
    },
    boyd: {
      technologies: [
        "Liquid Cooling CDUs & Cold Plates",
        "Two-Phase (Heat Pipes/Vapor Chambers)",
        "EMI/Sealing Materials",
        "Thermal Interface Materials",
      ],
      applications: [
        "Data Centers & Networks",
        "Semiconductor",
        "eMobility & Automotive",
      ],
      geographies: [
        "North America",
        "Asia Pacific (Vietnam/China)",
        "Europe (Poland)",
      ],
      segments: [
        "Hyperscale & Colocation",
        "Automotive/EV OEMs",
        "Semiconductor OEMs",
      ],
      useCases: [
        "2MW CDUs for AI racks",
        "Direct-to-chip cold plates",
        "EV battery thermal protection",
      ],
    },
  };

  const companies = [
    { id: "schneider", name: "Schneider Electric" },
    { id: "nvent", name: "nVent" },
    { id: "boyd", name: "Boyd Corporation" },
  ];

  const renderHeatMapItems = (items) => {
    return items.map((item, idx) => {
      const itemKey = item.dataKey || `item-${idx}`;
      const isSelected = selectedHeatMapItem === itemKey;

      return (
        <div key={idx} className="mb-3">
          <div
            className={`heatmap-item bg-${
              item.color
            } bg-opacity-10 border-start border-${
              item.color
            } border-4 p-2 rounded cursor-pointer ${
              isSelected ? "shadow selected-item" : ""
            }`}
            onClick={() => setSelectedHeatMapItem(isSelected ? null : itemKey)}
            style={{ cursor: "pointer" }}
          >
            <div className="d-flex justify-content-between align-items-center">
              <div className="flex-grow-1">
                <div className="comp_head_new">{item.name}</div>
                <div className="comp_subhead_new">
                  {item.players} player{item.players > 1 ? "s" : ""} •{" "}
                  {item.players === 4
                    ? "Very crowded"
                    : item.players === 3
                    ? "Competitive"
                    : item.players === 2
                    ? "Moderate"
                    : item.note || "Unique"}
                </div>
                {item.marketSize && (
                  <div className="comp_head_number">
                    {item.marketSize}
                  </div>
                )}
              </div>
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  setSelectedDetailModal(itemKey);
                }}
                className="btn btn-sm btn-link p-0 ms-2"
                title="Click for detailed analysis"
                style={{ fontSize: "1.2rem" }}
              >
                <Search size={18} className="text-primary" />
              </button>
            </div>
          </div>

          {isSelected && (
            <div className="mt-2 ms-2 p-3 bg-white rounded border border-primary border-2 shadow-sm">
              <div className="small fw-bold mb-2">
                Companies competing here:
              </div>
              <div className="d-flex flex-wrap gap-1">
                {item.companies.map((company, compIdx) => (
                  <span
                    key={compIdx}
                    className={`badge ${
                      company === "nVent"
                        ? "bg-primary"
                        : company === "Schneider Electric"
                        ? "bg-danger"
                        : company === "Boyd Corporation"
                        ? "bg-info"
                        : "bg-secondary"
                    }`}
                  >
                    {company}
                  </span>
                ))}
              </div>
              {item.note && (
                <div className="small text-primary fst-italic mt-2">
                  💡 {item.note}
                </div>
              )}
            </div>
          )}
        </div>
      );
    });
  };

  return (
    <div>
      <style>{`
     
        .selected-item {
          transform: scale(1.05);
        }
        .cursor-pointer {
          cursor: pointer;
        }
        .focus-badge {
          font-size: 0.7rem;
          padding: 0.25rem 0.5rem;
          border-radius: 0.25rem;
          display: inline-block;
          margin: 0.15rem;
        }
        .comp_map_block{
          border-bottom: 1px solid #e9ecef;
          background-color: #fff;
          padding: 12px 16px;
          border: solid 1px #98a4aa33;
          border-radius: 8px;
          margin-bottom: 10px;
        }
        .comp_subhead{
          font-size:13px;
          font-weight:600 !Important;
        }
        .comp_head_new{
          color:#2d343d !important;
          font-size:12px;
          line-height:17.5px;
          font-weight:600;
          margin-bottom:5px;
        }
        .comp_subhead_new{
          color:#64748B !important;
          font-size:12px;
          line-height:17.5px;
          font-weight:400;
          margin-bottom:5px;

        }
        .comp_head_number{
          color:#363b4b !important;
          font-size:13px;
          line-height:17.5px;
          font-weight:600;
        }
        .bg-custom-danger{
          background-color: #fef2f2;
          border-left: 4px solid #b91c1c !important;
        }
        .bg-custom-danger-only{
          background-color: #b91c1c;
        }
        .bg-custom-info{
          background-color: #eff6ff;
          border-left: 4px solid #1e74e6 !important;
        }
        .bg-custom-info-only{
          background-color: #1e74e6;
        }
        .bg-custom-success{
          background-color: #f0fdf4;
          border-left: 4px solid #15803d !important;
        }
        .bg-custom-success-only{
          background-color: #15803d;
        }
        .bg-custom-warning-only{
          background-color: #CFAB69;
        }
        .bg-custom-warning{
          background-color: #feebc89c;
          border-left: 4px solid #CFAB69 !important;
        }
        .score_inter{
          font-weight: 400;
    font-size: 12px;
    background-color: #f0f4fa;
    line-height: 1.4;
    color: rgba(0, 0, 0, 0.6);
    /* margin-top: 5px; */
    margin-bottom: 20px;
    padding: 12px 8px 12px 12px;
    border-radius: 8px;
        }
        .custom-resizable-table-th-content {
          justify-content: unset;
      }
      .custom_highlight_table{
        background-color:#f0f4fa;
      }
        
      `}</style>

      <DetailModal
        selectedDetailModal={selectedDetailModal}
        setSelectedDetailModal={setSelectedDetailModal}
      />

      {/* Competition Heat Map */}
      <div className="card border-0 mb-4 ">
        <div className="card-body comp_map_block">
          <h3 className="exe_summary mb-4">Competition Heat Map</h3>

          <div className="row g-3">
            <div className="col-md-3">
              <div className="d-flex align-items-center gap-2 comp_subhead mb-3">
                <Zap size={16} />
                Technologies
              </div>
              {renderHeatMapItems(heatMapData.technologies)}
            </div>

            <div className="col-md-3">
              <div className="d-flex align-items-center gap-2 comp_subhead fw-bold small mb-3">
                <Box size={16} />
                Applications
              </div>
              {renderHeatMapItems(heatMapData.applications)}
            </div>

            <div className="col-md-3">
              <div className="d-flex align-items-center gap-2 comp_subhead fw-bold small mb-3">
                <Globe size={16} />
                Geographies
              </div>
              {renderHeatMapItems(heatMapData.geographies)}
            </div>

            <div className="col-md-3">
              <div className="d-flex align-items-center gap-2 comp_subhead fw-bold small mb-3">
                <Users size={16} />
                Customer Segments
              </div>
              {renderHeatMapItems(heatMapData.segments)}
            </div>
          </div>

          <div className="row g-2 p-3 bg-light rounded mt-3">
            <div className="col-3 mt-0">
              <div className="d-flex align-items-center gap-2 small">
                <div
                  className="bg-custom-danger-only"
                  style={{ width: "1rem", height: "1rem", borderRadius: "2px" }}
                ></div>
                <div>
                  <div className="fw-bold font13">Very Crowded</div>
                  <div className="text-muted font13">4 players</div>
                </div>
              </div>
            </div>
            <div className="col-3 mt-0">
              <div className="d-flex align-items-center gap-2 small">
                <div
                  className="bg-custom-info-only"
                  style={{ width: "1rem", height: "1rem", borderRadius: "2px" }}
                ></div>
                <div>
                  <div className="fw-bold font13">Competitive</div>
                  <div className="text-muted font13">3 players</div>
                </div>
              </div>
            </div>
            <div className="col-3 mt-0">
              <div className="d-flex align-items-center gap-2 small">
                <div
                  className="bg-custom-warning-only"
                  style={{ width: "1rem", height: "1rem", borderRadius: "2px" }}
                ></div>
                <div>
                  <div className="fw-bold font13">Moderate</div>
                  <div className="text-muted font13">2 players</div>
                </div>
              </div>
            </div>
            <div className="col-3 mt-0">
              <div className="d-flex align-items-center gap-2 small">
                <div
                  className="bg-custom-success-only"
                  style={{ width: "1rem", height: "1rem", borderRadius: "2px" }}
                ></div>
                <div>
                  <div className="fw-bold font13">Unique/Leader</div>
                  <div className="text-muted font13">1 player</div>
                </div>
              </div>
            </div>
          </div>

          <div className="score_inter mt-2 mb-2">
            <div className="d-flex gap-2">
              {/* <Target size={20} className="flex-shrink-0 mt-1" /> */}
              <img src="/img/bullseye.png" width="16" height="16"/>
              <div className="small">
                <strong>Strategic Heat Map Insights:</strong> Industry & Automation ($276.6B) and EV Charging Infrastructure ($121.9B) are the largest applications and remain contested with 2 players each.{" "}
                <strong>
                  CRITICAL: Asia Pacific ($53.6B, ~7%+ CAGR) is THE growth opportunity.
                </strong>{" "}
                <strong>
                  GREEN OCEANS: Industrial Heat-Tracing ($3.4B, dominant nVent share) and LV/MV Busway pockets in North America offer protected positions to defend and expand.
                </strong>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Where Each Company Focuses */}
      <div className="card border-0  mb-4">
        <div className="card-body comp_map_block">
          <h3 className="exe_summary mb-4">Where Each Company Focuses</h3>
          <div className="table-responsive">
            <table className="table custom-resizable-table">
              <thead>
                <tr className="custom-resizable-table-header">
                  <th className=""><div className="custom-resizable-table-th-content">Company</div></th>
                  <th className="">
                    <div className="d-flex align-items-center gap-1 custom-resizable-table-th-content">
                      <Zap size={14} />
                      Top Technologies
                    </div>
                  </th>
                  <th className="">
                    <div className="d-flex align-items-center gap-1 custom-resizable-table-th-content">
                      <Box size={14} />
                      Top Applications
                    </div>
                  </th>
                  <th className="">
                    <div className="d-flex align-items-center gap-1 custom-resizable-table-th-content">
                      <Globe size={14} />
                      Top Geographies
                    </div>
                  </th>
                  <th className="">
                    <div className="d-flex align-items-center gap-1 custom-resizable-table-th-content">
                      <Users size={14} />
                      Top Segments
                    </div>
                  </th>
                  <th className="">
                    <div className="d-flex align-items-center gap-1 custom-resizable-table-th-content">
                      <Target size={14} />
                      Key Use Cases
                    </div>
                  </th>
                </tr>
              </thead>
              <tbody>
                {companies.map((company) => (
                  <tr
                    key={company.id}
                    className={ 
                      company.id === "nvent"
                        ? "table-primary border-start border-primary border-2 custom_highlight_table"
                        : ""
                    }
                    gtr-row
                  >
                    <td className="gtr-cell">
                      <div className="d-flex align-items-center gap-2">
                        {company.id === "nvent" && (
                          <span className="badge bg-primary">US</span>
                        )}
                        {company.name}
                      </div>
                    </td>
                    <td>
                      <div>
                        {focusAreas[company.id].technologies.map(
                          (tech, techIdx) => (
                            <span
                              key={techIdx}
                              className="focus-badge bg-purple bg-opacity-10 text-dark"
                            >
                              {tech}
                            </span>
                          )
                        )}
                      </div>
                    </td>
                    <td>
                      <div>
                        {focusAreas[company.id].applications.map(
                          (app, appIdx) => (
                            <span
                              key={appIdx}
                              className="focus-badge bg-primary bg-opacity-10 text-dark"
                            >
                              {app}
                            </span>
                          )
                        )}
                      </div>
                    </td>
                    <td>
                      <div>
                        {focusAreas[company.id].geographies.map(
                          (geo, geoIdx) => (
                            <span
                              key={geoIdx}
                              className="focus-badge bg-success bg-opacity-10 text-dark"
                            >
                              {geo}
                            </span>
                          )
                        )}
                      </div>
                    </td>
                    <td>
                      <div>
                        {focusAreas[company.id].segments.map((seg, segIdx) => (
                          <span
                            key={segIdx}
                            className="focus-badge bg-warning bg-opacity-10 text-dark"
                          >
                            {seg}
                          </span>
                        ))}
                      </div>
                    </td>
                    <td>
                      <div>
                        {focusAreas[company.id].useCases.map(
                          (useCase, ucIdx) => (
                            <span
                              key={ucIdx}
                              className="focus-badge bg-danger bg-opacity-10 text-dark"
                            >
                              {useCase}
                            </span>
                          )
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
};

export default MarketPosition;